import { AfterViewInit, Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { UserService } from 'src/app/services/user/user.service';
import { RedirectPreloaderService } from 'src/app/services/redirect-preloader.service';
import { take } from 'rxjs/operators';
import { ToastNotificationComponent } from 'src/app/shared/toast-notification/toast-notification.component';
import { ToastNotificationService } from 'src/app/services/toast-notification/toast-notification.service';
import { Subscription } from 'rxjs';
import { NativeSafeZoneService } from 'src/app/services/native-safe-zone.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { LOCAL_STORAGE_KEYS, LocalStorageService } from 'src/app/services/local-storage.service';
import { AuthLinksService } from 'src/app/services/auth-links.service';
import { SignalsService } from 'src/app/services/signals.service';
import { Browser } from '@capacitor/browser';
import { InAppBrowserAuthService } from 'src/app/services/in-app-browser-auth.service';
import { NavigateAfterLoginService } from 'src/app/services/navigate-after-login.service';
import { RouteReloadService } from 'src/app/services/reload-current-route';
import { NativeCheckService } from 'src/app/services/native-check.service';

const LOGIN_BACKGROUND_CLASS = 'login-background';
@Component({
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {
  public get bcasClientLoginUrl(): string {
    return this.authLinksService.bcasClientLoginUrl;
  }

  public get bcasConsultLoginUrl(): string {
    return this.authLinksService.bcasConsultLoginUrl;
  }

  private storageUserChange$: null | Subscription = null;

  public get isNativePhone(): boolean {
    return this.nativeCheckService.isNativePlatform();
  }

  public constructor(
    private router: Router,
    private userService: UserService,
    public redirectPreloaderService: RedirectPreloaderService,
    private route: ActivatedRoute,
    private toastService: ToastNotificationService,
    public nativeSafeZoneService: NativeSafeZoneService,
    public loggerService: LoggerService,
    private renderer: Renderer2,
    public localStorageService: LocalStorageService,
    private authLinksService: AuthLinksService,
    private signalsService: SignalsService,
    public inAppBrowserAuthService: InAppBrowserAuthService,
    public navigateAfterLoginService: NavigateAfterLoginService,
    public routeReloadService: RouteReloadService,
    private nativeCheckService: NativeCheckService,
  ) { }

  public ngOnInit(): void {
    const redirectUrl = this.localStorageService.getItemSync({ key: LOCAL_STORAGE_KEYS.REDIRECT_AFTER_LOGIN });
    if(this.userService.loggedUser) {
      if (redirectUrl) {
        this.navigateAfterLoginService.checkRedirectAndReloadIfExist(redirectUrl);
      } else {
        this.router.navigate(['/summary'], { replaceUrl: true });
      }
    }

    if(!this.nativeCheckService.isNativePlatform()) {
      this.initStorageKeyWatcher();
    }

    // route for instant redirect from portal to login without explicit click
    this.route.queryParams.pipe(take(1)).subscribe((params: Params) => {
      if(params['redirectOnly'] && !this.nativeCheckService.isNativePlatform()) {
        window.location.href = this.bcasClientLoginUrl;
      }
      if(params['showNoBwmError']) {
        this.router.navigate(['/login'], { replaceUrl: true });
        this.toastService.addNotification({
          title: 'toastNotifications.headers.authError',
          message: 'toastNotifications.messages.notBwmAccount',
          type: 'warning',
          disableAutohide: true,
          component: ToastNotificationComponent
        });
      }
      if(params['idleLogout']) {
        this.router.navigate(['/login'], { replaceUrl: true });
        this.toastService.addNotification({
          title: 'toastNotifications.headers.idleLogouted',
          message: 'toastNotifications.messages.idleLogout',
          type: 'warning',
          disableAutohide: true,
          component: ToastNotificationComponent
        });
      }
    });

    this.storageUserChange$ = this.signalsService.storageChanged$.subscribe(() => this.reloadIfUserChanged());
  }

  public openWorkplace(): void {
    Browser.open({ url: this.bcasConsultLoginUrl });
  }

  private reloadIfUserChanged(): void {
    if(this.userService.loggedUser) {
      this.routeReloadService.reloadCurrent();
    }
  }

  public ngAfterViewInit(): void {
    this.renderer.addClass(document.body, LOGIN_BACKGROUND_CLASS);
  }


  private initStorageKeyWatcher(): void {
    window.addEventListener('storage', this.storageChanged.bind(this));
  }

  private storageChanged(event: StorageEvent): void {
    if(event.key === LOCAL_STORAGE_KEYS.USER_LOCAL_STORAGE_KEY && event.newValue?.length && event.newValue !== event.oldValue) {
      this.routeReloadService.reloadCurrent();
    }
  }

  public ngOnDestroy(): void {
    this.storageUserChange$?.unsubscribe();
    this.storageUserChange$ = null;
    this.renderer.removeClass(document.body, LOGIN_BACKGROUND_CLASS);
  }
}
